import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ExternalLink, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Body } from "@/pages/ContentPage";

const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";
const EMPTY = { title: "", subtitle: "", intro: "", sections: [], cta_label: "", cta2_label: "" };

export function PageAdmin({ slug = "legalization", title = "Strona „Legalizacja dokumentów”", path = "/biuro-wirtualne-warszawa/legalizacja-dokumentow", hint = "" }) {
  const [page, setPage] = useState(null);
  const [saved, setSaved] = useState("");
  const [lang, setLang] = useState("pl");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    setPage(null);
    api.get(`/admin/pages/${slug}`).then((r) => { setPage(r.data); setSaved(JSON.stringify(r.data)); }).catch(() => toast.error("Nie udało się pobrać treści strony."));
  }, [slug]);
  if (!page) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-600" /></div>;

  const p = { ...EMPTY, ...page[lang] };
  const set = (patch) => setPage({ ...page, [lang]: { ...p, ...patch } });
  const setSection = (i, patch) => set({ sections: p.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const move = (i, d) => {
    const s = [...p.sections];
    const [x] = s.splice(i, 1);
    s.splice(i + d, 0, x);
    set({ sections: s });
  };
  const dirty = JSON.stringify(page) !== saved;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/pages/${slug}`, { pl: page.pl, en: page.en });
      const next = { pl: r.data.pl, en: r.data.en };
      setPage(next);
      setSaved(JSON.stringify(next));
      toast.success("Strona zapisana.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nie udało się zapisać.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="legalization-admin" className="space-y-5">
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-lg">{title}</h2>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed max-w-2xl">
              Treść podstrony <code className="font-mono">{path}</code>{hint && <> ({hint})</>}. Akapity oddzielaj pustą linią; listy: „- ” lub „1. ”; wytłuszczenie: <code className="font-mono">**tekst**</code>; tabela: wiersze zaczynające się od „|” (pierwszy to nagłówek); linki: <code className="font-mono">[tekst](https://adres)</code>.
              Pozostałe języki strony korzystają z wersji angielskiej (gdy pusta — polskiej).
            </p>
          </div>
          <a href={path} target="_blank" rel="noopener noreferrer" data-testid="legalization-open-page" className={`${btn} border border-border hover:bg-muted`}><ExternalLink size={13} /> Otwórz stronę</a>
        </div>
        <div className="flex flex-wrap gap-2">
          {[["pl", "Polski"], ["en", "English"]].map(([c, l]) => (
            <button key={c} type="button" data-testid={`legalization-lang-${c}`} onClick={() => setLang(c)} className={`${btn} border ${lang === c ? "bg-brand-navy text-white border-brand-navy dark:bg-white dark:text-slate-900" : "border-border hover:bg-muted"}`}>{l}</button>
          ))}
          <button type="button" data-testid="legalization-toggle-preview" onClick={() => setPreview(!preview)} className={`${btn} border ml-auto ${preview ? "bg-brand-teal text-white border-brand-teal" : "border-border hover:bg-muted"}`}>{preview ? "Ukryj podgląd" : "Pokaż podgląd"}</button>
        </div>
      </div>

      <div className={`grid gap-5 ${preview ? "lg:grid-cols-2" : ""}`}>
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-3">
            <Field label="Nadtytuł (mała etykieta)"><Input data-testid="legalization-subtitle" value={p.subtitle} onChange={(e) => set({ subtitle: e.target.value })} className="bg-background text-sm" /></Field>
            <Field label="Tytuł strony"><Input data-testid="legalization-title-input" value={p.title} onChange={(e) => set({ title: e.target.value })} className="bg-background text-sm font-semibold" /></Field>
            <Field label="Wstęp"><Textarea data-testid="legalization-intro-input" rows={5} value={p.intro} onChange={(e) => set({ intro: e.target.value })} className="bg-background text-sm" /></Field>
            <Field label="Przycisk pod wstępem (prowadzi do formularza kontaktowego)"><Input data-testid="legalization-cta-input" value={p.cta_label} onChange={(e) => set({ cta_label: e.target.value })} className="bg-background text-sm" /></Field>
            <Field label="Przycisk na końcu strony (prowadzi do formularza kontaktowego)"><Input data-testid="legalization-cta2-input" value={p.cta2_label} onChange={(e) => set({ cta2_label: e.target.value })} className="bg-background text-sm" /></Field>
          </div>
          {p.sections.map((s, i) => (
            <div key={i} data-testid={`legalization-section-editor-${i + 1}`} className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Sekcja {i + 1}</span>
                <div className="flex gap-1">
                  <button type="button" aria-label="W górę" data-testid={`legalization-section-up-${i + 1}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30"><ArrowUp size={14} /></button>
                  <button type="button" aria-label="W dół" data-testid={`legalization-section-down-${i + 1}`} disabled={i === p.sections.length - 1} onClick={() => move(i, 1)} className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30"><ArrowDown size={14} /></button>
                  <button type="button" aria-label="Usuń" data-testid={`legalization-section-delete-${i + 1}`} onClick={() => set({ sections: p.sections.filter((_, j) => j !== i) })} className="p-1.5 rounded-full text-brand-red hover:bg-brand-red/10"><Trash2 size={14} /></button>
                </div>
              </div>
              <Input data-testid={`legalization-section-heading-${i + 1}`} placeholder="Nagłówek sekcji" value={s.heading} onChange={(e) => setSection(i, { heading: e.target.value })} className="bg-background text-sm font-semibold" />
              <Textarea data-testid={`legalization-section-body-${i + 1}`} rows={6} placeholder="Treść sekcji…" value={s.body} onChange={(e) => setSection(i, { body: e.target.value })} className="bg-background text-sm" />
            </div>
          ))}
          <button type="button" data-testid="legalization-add-section" disabled={p.sections.length >= 30} onClick={() => set({ sections: [...p.sections, { heading: "", body: "" }] })} className={`${btn} border border-dashed border-border hover:bg-muted w-full justify-center py-3`}><Plus size={13} /> Dodaj sekcję</button>
        </div>

        {preview && (
          <div data-testid="legalization-preview" className="bg-white/85 dark:bg-slate-900/85 border border-border rounded-2xl p-6 sm:p-8 lg:sticky lg:top-6 self-start max-h-[80vh] overflow-y-auto">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold"><span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-red mr-3 align-middle" />{p.subtitle}</p>
            <h1 className="font-display font-bold tracking-tight text-3xl mt-3">{p.title}</h1>
            <div className="mt-4"><Body text={p.intro} /></div>
            {p.cta_label && <span className="mt-4 inline-flex bg-brand-red text-white text-sm font-semibold px-6 py-3 rounded-full">{p.cta_label}</span>}
            <div className="mt-6 space-y-4">
              {p.sections.map((s, i) => (
                <div key={i} className="border border-slate-200 dark:border-slate-800 rounded-xl p-4">
                  {s.heading && <h2 className="font-display font-bold text-base mb-2">{s.heading}</h2>}
                  <Body text={s.body} />
                </div>
              ))}
            </div>
            {(p.cta2_label || p.cta_label) && <span className="mt-6 inline-flex bg-brand-teal text-white text-sm font-semibold px-6 py-3 rounded-full">{p.cta2_label || p.cta_label}</span>}
          </div>
        )}
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <div className="flex items-center gap-3 bg-card border border-border rounded-full pl-4 pr-2 py-2 shadow-lg">
          {dirty && <span data-testid="legalization-unsaved" className="text-xs text-amber-600 dark:text-amber-400">Niezapisane zmiany</span>}
          <button type="button" data-testid="legalization-save-button" onClick={save} disabled={busy || !dirty} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Zapisz stronę
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground block mb-1">{label}</label>
      {children}
    </div>
  );
}
